import React from 'react';
import type { UIComponent } from '../schemas/base';
import { TableRenderer } from '../components/table/TableRenderer';
import { ChartRenderer } from '../components/chart/ChartRenderer';
import { CardRenderer } from '../components/card/CardRenderer';
import type { TableComponent } from '../schemas/table';
import type { ChartComponent } from '../schemas/chart';
import type { CardComponent } from '../schemas/card';

/**
 * Component renderer function signature
 */
export type ComponentRenderer<T extends UIComponent = UIComponent> = React.FC<{
    component: T;
    onAction?: (action: string) => void;
    className?: string;
}>;

/**
 * Registry of component renderers
 */
const registry: Map<string, ComponentRenderer<UIComponent>> = new Map();

/**
 * Register a component renderer
 */
export function registerComponent<T extends UIComponent>(
    type: T['type'],
    renderer: ComponentRenderer<T>
): void {
    registry.set(type, renderer as ComponentRenderer<UIComponent>);
}

/**
 * Get a component renderer by type
 */
export function getRenderer(type: string): ComponentRenderer<UIComponent> | undefined {
    return registry.get(type);
}

/**
 * Check if a renderer exists for a type
 */
export function hasRenderer(type: string): boolean {
    return registry.has(type);
}

/**
 * Get all registered types
 */
export function getRegisteredTypes(): string[] {
    return Array.from(registry.keys());
}

// Register default components
registerComponent<TableComponent>('table', TableRenderer);
registerComponent<ChartComponent>('chart', ChartRenderer);
registerComponent<CardComponent>('card', CardRenderer);

export { registry };
